import { useTheme } from '@bucketplace/tokens';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { RefObject, useEffect, useRef } from 'react';

import { REBRANDING_THEME } from '@/brand/common/style';

const THEME_CHANGE_PROGRESS = {
  HERO_SECTION: { theme: 'dark', progress: 0.3 },
  LIFE_EVENT_SECTION: { theme: 'light', progress: 0.62 },
} as const;

function getThemeChangeProgress(progress: number): 'light' | 'dark' {
  if (progress < THEME_CHANGE_PROGRESS.HERO_SECTION.progress) {
    return 'light';
  }

  if (progress < THEME_CHANGE_PROGRESS.LIFE_EVENT_SECTION.progress) {
    return THEME_CHANGE_PROGRESS.HERO_SECTION.theme;
  }

  return THEME_CHANGE_PROGRESS.LIFE_EVENT_SECTION.theme;
}

export function useThemeControl(container: RefObject<HTMLElement>): void {
  const { setAppearance } = useTheme<typeof REBRANDING_THEME>();
  const currentThemeRef = useRef<'light' | 'dark'>('light');
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !container.current) {
      return undefined;
    }
    gsap.registerPlugin(ScrollTrigger);

    // 기존 ScrollTrigger가 있으면 제거
    if (scrollTriggerRef.current) {
      scrollTriggerRef.current.kill();
    }

    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: container.current,
      start: 'top top',
      end: '+=2700%',
      onUpdate: ({ progress }) => {
        const targetTheme = getThemeChangeProgress(progress);

        if (currentThemeRef.current !== targetTheme) {
          currentThemeRef.current = targetTheme;
          setAppearance(targetTheme);
        }
      },
    });

    return () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
        scrollTriggerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);
}
